import { ParsedUrlQueryInput } from 'querystring';

import { cookiesNames } from '@zmyle/webkit-http-utils';
import { type ClassValue, clsx as _clsx } from 'clsx';
import { isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { marked } from 'marked';
import { parseCookies } from 'nookies';
import platform from 'platform';
import { twMerge } from 'tailwind-merge';
import xss from 'xss';

export const genId = () => {
  return Math.random()
    .toString(32)
    .replace(/^.{0,2}/, '');
};

interface FormatNumberParams {
  value: number;
  minDecimalDigits?: number;
  maxDecimalDigits?: number;
}

export const formatNumber = (params: FormatNumberParams) => {
  const { value, maxDecimalDigits = 2, minDecimalDigits } = params;

  return new Intl.NumberFormat('DE-de', {
    minimumFractionDigits: minDecimalDigits,
    maximumFractionDigits: maxDecimalDigits,
  }).format(value);
};

interface FormatCurrencyParams {
  value: number;
  minDecimalDigits?: number;
  maxDecimalDigits?: number;
}

export const formatCurrency = (params: FormatCurrencyParams) => {
  const { value, maxDecimalDigits = 2, minDecimalDigits } = params;

  return new Intl.NumberFormat('DE-de', {
    minimumFractionDigits: minDecimalDigits,
    maximumFractionDigits: maxDecimalDigits,
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

export const formatFloatToInteger = (value: number | string) => {
  const multipliedValue = (Number(value) * 100).toFixed();

  return parseInt(multipliedValue, 10);
};

export const isServer = () => typeof window === 'undefined';

interface SendEmailParams {
  to?: string;
  subject?: string;
  body: string;
}

export const sendEmail = (params: SendEmailParams): void => {
  const { to = ' ', subject = '', body } = params;

  const anchor = document.createElement('a');

  anchor.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const multiplyBy100 = (value: number | string) => {
  const multipliedValue = (Number(value) * 100).toFixed(2);

  return Number(multipliedValue);
};

export const divideBy100 = (value: number | string) => {
  const dividedValue = (Number(value) / 100).toFixed(4);

  return Number(dividedValue);
};

interface FindStoreCategoryParams {
  categoryId: string;
  categories: Category[];
}

export const findStoreCategoryById = (
  params: FindStoreCategoryParams,
): Category | null => {
  const { categoryId, categories } = params;

  return categories.reduce(
    (acc, category) => {
      if (acc) {
        return acc;
      }
      if (category.id === categoryId) {
        return category;
      }
      return findStoreCategoryById({
        categories: category.children,
        categoryId,
      });
    },
    null as Category | null,
  );
};

export const parseMarkdownToHtml = (markdown: string) => {
  if (!markdown) return '';

  const html = marked.parse(markdown, { async: false });
  const sanitisedHtml = xss(html);

  return sanitisedHtml;
};

export const parseMarkdownToPlainText = (markdown: string): string => {
  if (!markdown) return '';

  const html = parseMarkdownToHtml(markdown);

  const withLineBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|tr|h[1-6]|blockquote|pre)>/gi, '\n');

  const withoutTags = withLineBreaks.replace(/<[^>]*>/g, '');

  const decoded = withoutTags
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

  const collapsed = decoded.replace(/\n{3,}/g, '\n\n');

  return collapsed.trim();
};

export const isMacOS = () => {
  if (typeof window === 'undefined') return false;

  const { os = '' } = platform;

  return /^(mac\s?)?os\sx/i.test(os.toString());
};

export const isZmyleCookie = (
  value?: string | Record<string, any>,
): value is ZmyleCookie => {
  if (!value || (typeof value === 'object' && !('consents' in value))) {
    return false;
  }

  try {
    if (typeof value !== 'string') return false;

    const parsedCookie = JSON.parse(value);

    return Boolean('consents' in parsedCookie);
  } catch {
    return false;
  }
};

export const getZmyleCookie = (
  params?: Parameters<typeof parseCookies>[0],
): ZmyleCookie => {
  const cookies = parseCookies(params);

  const zmyleCookie = cookies[cookiesNames.zmyleCookie];

  return isZmyleCookie(zmyleCookie)
    ? JSON.parse(zmyleCookie)
    : { consents: { marketing: [], media: [] } };
};

export const getDomainFromUrl = (website: string) => {
  const regexUrlPrefix = /^(?:https?:\/\/)?(?:www\.)?/i;

  return website?.replace(regexUrlPrefix, '');
};

interface ParseDateFromServerOptions {
  timezoneId?: string;
}

export const parseDateFromServer = (
  date?: string | null,
  options?: ParseDateFromServerOptions,
) => {
  const { timezoneId = 'Europe/Berlin' } = options || {};

  if (!date) return null;

  const parsedDate = parseISO(date);

  return utcToZonedTime(parsedDate, timezoneId, {
    locale: de,
  });
};

interface FormatDateToAppOptions {
  timezoneId?: string;
}

export const formatDateToApp = (
  date?: Date | string | null,
  options?: FormatDateToAppOptions,
) => {
  const { timezoneId = 'Europe/Berlin' } = options || {};

  if (!date) return '';

  const dateToFormat =
    typeof date === 'string' ? parseDateFromServer(date) : date;

  if (!isValid(dateToFormat) || !dateToFormat) return '';

  const zonedDateTime = utcToZonedTime(dateToFormat, timezoneId, {
    locale: de,
  });

  return formatInTimeZone(zonedDateTime, timezoneId, 'dd/MM/yyyy', {
    locale: de,
  });
};

export const buildQueryParams = (params: Record<string, unknown>) => {
  return Object.entries(params).reduce((acc, current, index) => {
    const prefix = index > 0 ? '&' : '';
    const [key, value] = current;

    const encodedValue = encodeURI(String(value));

    return `${acc}${prefix}${key}=${encodedValue}`;
  }, '');
};

interface ResolvePathParams {
  pathname: string;
  query?: ParsedUrlQueryInput;
}

export const resolvePath = (route: ResolvePathParams) => {
  const { pathname, query = {} } = route;

  const urlParams = pathname.match(/\[(.*?)\]/g) || [];
  let newUrlPath = pathname;
  const remainingQuery: Record<string, any> = { ...query };

  urlParams.forEach(param => {
    const paramName = param.slice(1, -1);
    const paramValue = query[paramName];

    newUrlPath = newUrlPath.replace(param, String(paramValue));
    delete remainingQuery[paramName];
  });

  const queryString = buildQueryParams(remainingQuery);

  if (queryString) newUrlPath += `?${queryString}`;

  return newUrlPath;
};

interface DoesRouteMatchParams {
  signature: string;
  route: string | null;
}

export const doesRouteMatch = (params: DoesRouteMatchParams) => {
  const { signature, route } = params;

  const signatureParams = signature.match(/\[(.*?)\]/g) || [];
  let newSignaturePath = signature;

  signatureParams.forEach(param => {
    newSignaturePath = newSignaturePath.replace(param, `([^/]+)`);
  });

  const regex = new RegExp(`^${newSignaturePath}$`);
  return regex.test(String(route));
};

export function clsx(...inputs: ClassValue[]) {
  return twMerge(_clsx(inputs));
}

export const formatDateAsZmyleApi = (date: Date) => {
  if (!isValid(date)) return '';

  return date
    .toISOString()
    .replace(/-|:|\./g, '')
    .replace(/\d{3}Z$/, 'Z');
};
